/**
 * Préstamos (Finanzas · Inversiones, regla 6, decidido con Alex el 11 oct 2026).
 *
 * El dueño sabe lo que le prestaron, la cuota que paga cada mes y cuántas
 * cuotas son. Con eso Alexia saca la tasa (sistema francés, cuota fija), la
 * enseña para que la confirme y cada mes separa la cuota en intereses +
 * capital. Los INTERESES van solos al Estado de resultados («Intereses de
 * préstamos»); la cuota NO es un gasto.
 *
 * Funciones puras: las usan la API, la demo y el motor de P&L (finance.ts).
 * Este módulo no importa de finance.ts para no crear un ciclo.
 */
import { addMonths, monthDiff, monthOf } from './period';
import type { ParteDelLocal } from './types';
import { round } from './units';

/** La parte de un local en `month` (1 si no es una parte de algo «de todos»). */
export function parteDelMes(x: { parteDelLocal?: ParteDelLocal }, month: string): number {
  const p = x.parteDelLocal;
  if (!p) return 1;
  return p.porMes[month] ?? p.resto;
}

export interface Loan {
  tenantId: string;
  loanId: string;
  /** Local al que pertenece. Ausente = principal; `grupo` = de todos. */
  locationId?: string;
  /** Cómo lo llama el dueño: «Préstamo del banco». */
  name?: string;
  /** Cuánto te prestaron. */
  principal: number;
  /** Cuota fija al mes (intereses + capital). */
  payment: number;
  /** Número de cuotas. */
  installments: number;
  /** Fecha de la primera cuota (YYYY-MM-DD): esa cuota cae en ese mes. */
  startDate: string;
  /** Inversión que se pagó con este préstamo, si la hay. */
  investmentId?: string;
  notes?: string;
  /** Solo en lecturas de un local: su parte de este préstamo «de todos», mes a mes. Nunca se guarda. */
  parteDelLocal?: ParteDelLocal;
}

/**
 * Tasa MENSUAL que hace que `installments` cuotas de `payment` devuelvan
 * `principal` (sistema francés):
 *
 *   principal = payment · (1 − (1 + r)^−n) / r
 *
 * Sin solución cerrada: bisección entre 0 y 100 % mensual. Si la suma de
 * las cuotas no llega a lo prestado, no hay tasa posible (devuelve null);
 * si la iguala justo, la tasa es 0.
 */
export function loanMonthlyRate(principal: number, payment: number, installments: number): number | null {
  if (!(principal > 0) || !(payment > 0) || !(installments >= 1)) return null;
  const total = payment * installments;
  if (total < principal - 0.005) return null;
  if (Math.abs(total - principal) < 0.005) return 0;
  const present = (r: number) => payment * (1 - Math.pow(1 + r, -installments)) / r;
  let lo = 1e-9;
  let hi = 1;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    // Más tasa → menos valor presente de las cuotas.
    if (present(mid) > principal) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Tasa anual (nominal, mensual × 12) en %, la que se le enseña al dueño. */
export function loanAnnualRatePct(loan: Pick<Loan, 'principal' | 'payment' | 'installments'>): number | null {
  const r = loanMonthlyRate(loan.principal, loan.payment, loan.installments);
  return r == null ? null : round(r * 12 * 100, 2);
}

export interface LoanInstallment {
  /** 1..n */
  number: number;
  month: string; // YYYY-MM
  interest: number;
  capital: number;
  /** Lo que queda por devolver después de esta cuota. */
  balance: number;
}

/**
 * Cuadro de cuotas. La última absorbe los céntimos para que el saldo cierre
 * en 0 exacto. Préstamo sin tasa posible: todo capital (no inventamos
 * intereses), y la pantalla avisa de que los datos no cuadran.
 */
export function loanSchedule(loan: Pick<Loan, 'principal' | 'payment' | 'installments' | 'startDate'>): LoanInstallment[] {
  const n = Math.max(0, Math.floor(loan.installments));
  const r = loanMonthlyRate(loan.principal, loan.payment, n) ?? 0;
  const start = monthOf(loan.startDate);
  const rows: LoanInstallment[] = [];
  let balance = loan.principal;
  for (let k = 1; k <= n; k += 1) {
    const interest = balance * r;
    let capital = loan.payment - interest;
    if (k === n || capital > balance) capital = balance;
    balance = Math.max(0, balance - capital);
    rows.push({
      number: k,
      month: addMonths(start, k - 1),
      interest: round(interest, 2),
      capital: round(capital, 2),
      balance: round(balance, 2),
    });
  }
  return rows;
}

/** Intereses de UN préstamo en un mes (0 fuera de su plazo). */
export function loanInterestForMonth(loan: Loan, month: string): number {
  const start = monthOf(loan.startDate);
  if (month < start) return 0;
  const k = monthDiff(start, month) + 1;
  if (k > loan.installments) return 0;
  return (loanSchedule(loan)[k - 1]?.interest ?? 0) * parteDelMes(loan, month);
}

/**
 * Intereses de préstamos de un mes: la línea «Intereses de préstamos» del
 * Estado de resultados. La consume el motor de P&L (finance.ts) y la pantalla
 * del Estado de resultados (bloque R).
 */
export function interesesDePrestamos(loans: readonly Loan[] | undefined, month: string): number {
  if (!loans?.length) return 0;
  return round(loans.reduce((s, l) => s + loanInterestForMonth(l, month), 0), 2);
}

export interface LoanStatus {
  /** Cuotas que ya vencieron a ese mes (incluido). */
  paid: number;
  installments: number;
  /** Lo que queda por devolver tras la última cuota vencida. */
  remaining: number;
  interestThisMonth: number;
  capitalThisMonth: number;
  annualRatePct: number | null;
  finished: boolean;
  /** Las cuotas no llegan a lo prestado: no hay tasa posible. */
  inconsistent: boolean;
}

/** Cómo va un préstamo a un mes dado: «2 de 24 cuotas · quedan $11,114». */
export function loanStatus(loan: Loan, month: string): LoanStatus {
  const schedule = loanSchedule(loan);
  const start = monthOf(loan.startDate);
  const paid = month < start ? 0 : Math.min(loan.installments, monthDiff(start, month) + 1);
  const current = paid > 0 && paid <= schedule.length ? schedule[paid - 1] : undefined;
  const thisMonth = schedule.find((row) => row.month === month);
  return {
    paid,
    installments: loan.installments,
    remaining: paid === 0 ? round(loan.principal, 2) : current?.balance ?? 0,
    interestThisMonth: thisMonth?.interest ?? 0,
    capitalThisMonth: thisMonth?.capital ?? 0,
    annualRatePct: loanAnnualRatePct(loan),
    finished: paid >= loan.installments,
    inconsistent: loanMonthlyRate(loan.principal, loan.payment, loan.installments) == null,
  };
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

const PALABRAS_CUOTA = /\b(cuota|prestamo|credito|hipoteca|financiamiento|financiacion|mensualidad)\b/;
const PALABRAS_BANCO = /\b(banco|bancaria|bancario|bbva|santander|banorte|banamex|hsbc|caixa|caixabank|sabadell|bankinter|scotiabank|bancolombia|davivienda|itau|galicia)\b/;

export interface ExpenseLike {
  concept?: string;
  supplierName?: string;
  category?: string;
  /** Importe sin IVA. */
  netAmount?: number;
  /** Total con IVA, si se conoce. */
  invoiceTotal?: number;
}

/**
 * ¿Este gasto es la cuota de un préstamo? (regla 6: «La cuota no es un gasto.
 * Si en Gastos aparece algo como "cuota préstamo" o del banco con el mismo
 * monto que la cuota, avisar»).
 *
 * Devuelve el préstamo al que se parece, o undefined. Casa si:
 *  - el monto (con o sin IVA) es el de la cuota (±1 %) y el texto suena a
 *    banco o a cuota, o la categoría es de intereses/comisiones del banco; o
 *  - el texto dice «cuota/préstamo/crédito…» aunque el monto no cuadre
 *    (se elige el préstamo de cuota más parecida).
 *
 * Pensada para Gastos (bloque G) y el escáner: aviso, nunca bloqueo.
 */
export function pareceCuotaDePrestamo(expense: ExpenseLike, loans: readonly Loan[]): Loan | undefined {
  if (!loans.length) return undefined;
  const texto = normalizar(`${expense.concept ?? ''} ${expense.supplierName ?? ''}`);
  const suenaACuota = PALABRAS_CUOTA.test(texto);
  const suenaABanco = PALABRAS_BANCO.test(texto) || expense.category === 'intereses' || expense.category === 'comisiones_bancarias';
  const montos = [expense.netAmount, expense.invoiceTotal].filter((m): m is number => typeof m === 'number' && m > 0);
  const mismoMonto = (loan: Loan) => montos.some((m) => Math.abs(m - loan.payment) <= loan.payment * 0.01);

  const porMonto = loans.find(mismoMonto);
  if (porMonto && (suenaACuota || suenaABanco)) return porMonto;
  if (suenaACuota && /\b(cuota|prestamo|credito|hipoteca)\b/.test(texto)) {
    if (porMonto) return porMonto;
    const referencia = montos[0];
    if (referencia == null) return loans[0];
    return [...loans].sort((a, b) => Math.abs(a.payment - referencia) - Math.abs(b.payment - referencia))[0];
  }
  return undefined;
}

/**
 * Intereses que el dueño ya registraba a mano en Gastos (categoría
 * «Intereses»). Al crear un préstamo se avisa para no contarlos dos veces.
 */
export function interesesAMano(
  expenses: ReadonlyArray<{ category: string; netAmount: number; date: string }>,
  desdeMes: string,
): { count: number; total: number } {
  const lista = expenses.filter((e) => e.category === 'intereses' && monthOf(e.date) >= desdeMes);
  return { count: lista.length, total: round(lista.reduce((s, e) => s + e.netAmount, 0), 2) };
}

/**
 * Intereses dos veces (revisión de Alex del 14 oct 2026): gastos de
 * «Intereses» anotados a mano EN LOS MESES QUE CUBRE el préstamo (de la
 * primera cuota a la última, sin pasar de `hastaMes`). Desde que existe el
 * préstamo, sus intereses van solos al resultado: esos gastos contarían dos
 * veces. Aviso al crearlo; no se borra nada solo.
 */
export function interesesEnMesesDelPrestamo<E extends { category: string; netAmount: number; date: string }>(
  expenses: readonly E[],
  loan: Pick<Loan, 'startDate' | 'installments'>,
  hastaMes: string,
): { gastos: E[]; meses: string[]; total: number } {
  const desde = monthOf(loan.startDate);
  const ultima = addMonths(desde, Math.max(1, loan.installments) - 1);
  const hasta = ultima < hastaMes ? ultima : hastaMes;
  const gastos = expenses
    .filter((e) => e.category === 'intereses' && monthOf(e.date) >= desde && monthOf(e.date) <= hasta)
    .sort((a, b) => a.date.localeCompare(b.date));
  const meses = [...new Set(gastos.map((e) => monthOf(e.date)))];
  return { gastos, meses, total: round(gastos.reduce((s, e) => s + e.netAmount, 0), 2) };
}
